import { useEffect, useState, type ReactNode } from 'react';
import { Link, useBlocker } from 'react-router';
import { Button } from '@/components/Button';
import { ApiError } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import s from '@/features/products/ProductFormPage.module.css';
import d from '@/features/admin/AssemblyLineSheet.module.css';

export const WRITES_ENABLED = import.meta.env.VITE_READONLY === '0';
export function writeFailure(cause: unknown) {
  const uncertain = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
  return { uncertain, message: `${cause instanceof Error ? cause.message : '操作失败'}${uncertain ? '；结果未确认，请刷新核对后再操作，暂勿重复提交。' : ''}` };
}

export function MaintenanceForm({ title, sub, back, dirty, busy, disabled, error, onSave, children }: {
  title: string; sub?: string; back: string; dirty: boolean; busy: boolean; disabled?: boolean; error?: string;
  onSave: () => void; children: ReactNode;
}) {
  const ff = useFormFactor();
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !busy && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  return <form className={`${s.page} ${ff === 'phone' ? s.phonePage : ''}`} onSubmit={(event) => { event.preventDefault(); if (!disabled && !busy && WRITES_ENABLED) onSave(); }}>
    <header className={s.head}><div><h1 title={title} style={ff === 'phone' ? { maxWidth: 'calc(100vw - 140px)', overflow: 'hidden', textOverflow: 'ellipsis' } : undefined}>{title}</h1>{sub && <p>{sub}</p>}</div><div className={s.actions}>
      <Link to={back} aria-disabled={busy} onClick={(event) => { if (busy) event.preventDefault(); }}>取消</Link>
      <button type="submit" className={s.primary} disabled={!WRITES_ENABLED || busy || disabled}>{busy ? '保存中…' : '保存'}</button>
    </div></header>
    <div className={s.scroll}>{children}{error && <p className={s.error} role="alert">{error}</p>}{!WRITES_ENABLED && <p className={s.readonly}>当前为只读模式，不能保存。</p>}</div>
    {ff === 'phone' && <footer className={s.phoneFooter}><Button type="submit" variant="primary" size="lg" block disabled={!WRITES_ENABLED || busy || disabled}>{busy ? '保存中…' : '保存'}</Button></footer>}
    {blocker.state === 'blocked' && <ConfirmWindow title="放弃未保存的修改？" onClose={() => blocker.reset()} onConfirm={() => blocker.proceed()} confirm="放弃修改"><p>离开后，本次未保存的内容将丢失。</p></ConfirmWindow>}
  </form>;
}

export function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className={s.card}><h2>{title}</h2><div className={s.grid}>{children}</div></section>;
}

export function ConfirmWindow({ title, onClose, onConfirm, confirm = '确认', disabled, children }: {
  title: string; onClose: () => void; onConfirm: () => void | Promise<void>; confirm?: string; disabled?: boolean; children: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  useEffect(() => {
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  }, [busy, onClose]);
  const submit = async () => {
    if (busy || disabled || uncertain) return;
    setBusy(true); setError('');
    try { await onConfirm(); } catch (cause) { const failure = writeFailure(cause); setError(failure.message); setUncertain(failure.uncertain); } finally { setBusy(false); }
  };
  return <div className={d.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}><section className={d.sheet} role="dialog" aria-modal="true" aria-label={title}>
    <header><h2>{title}</h2><button type="button" aria-label="关闭" disabled={busy} onClick={onClose}>×</button></header>
    <div className={d.body}>{children}{error && <p className={d.error} role="alert">{error}</p>}</div>
    <footer><button type="button" disabled={busy} onClick={onClose}>取消</button><button type="button" autoFocus className={d.primary} disabled={disabled || uncertain || busy} onClick={() => void submit()}>{busy ? '处理中…' : confirm}</button></footer>
  </section></div>;
}
